import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import host from '../../global';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { useTheme } from '../../hooks/useTheme';
import BehavioralAssessmentPanel from './BehavioralAssessmentPanel';

// Fetches this candidate's behavioral result, then hands it to the shared
// presentational panel (also used by PublicReport.jsx, which pre-fetches the
// same shapes via a different endpoint and doesn't need this fetch).
export default function BehavioralResult({ name, behavioral_percent, interviewId = null, data = undefined }) {
  const { interview_id: routeInterviewId } = useParams();
  // Public share-token route: the parent passes the real id (the route param is the
  // token) and pre-fetched `data` (the authed /get_behavioral_test_result is unavailable).
  const interview_id = interviewId || routeInterviewId;
  const { theme: themeMode } = useTheme();
  const [resultData, setResultData] = useState(data || null);
  const [loading, setLoading] = useState(!data);
  const accessToken = Cookies.get('accessToken');

  useEffect(() => {
    if (data) {
      setResultData(data);
      setLoading(false);
      return;
    }
    const fetchResult = async () => {
      try {
        const response = await axios.get(`${host}/get_behavioral_test_result`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { interview_id },
        });
        setResultData(response.data);
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              Cookies.remove('accessToken');
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error('An unexpected error occurred. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [interview_id, accessToken, data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <CircularProgress size={32} sx={{ color: '#C9A3FF' }} />
      </div>
    );
  }

  return (
    <BehavioralAssessmentPanel
      overallScore={resultData?.overall_score ?? behavioral_percent ?? 0}
      competencyScores={resultData?.competency_scores || []}
      questions={resultData?.questions || []}
      insights={resultData?.ai_insights}
      themeMode={themeMode}
      candidateName={name}
    />
  );
}
